import { useState, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useCurrency } from "../../utils";
import {
  getPlanPagosEdificios,
  crearPlanPagoEdificio,
  actualizarPlanPagoEdificio,
  anularPlanPagoEdificio,
} from "../../utils/planPagosEdificios";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";

const formatMontoDisplay = (value, pais) => {
  if (!value && value !== 0) return "";
  if (typeof value === "string") return value;
  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
};

const parseMontoInput = (displayValue, moneda) => {
  const clean = displayValue.replace(/[^\d,.]/g, "");
  if (!clean) return "";
  const lastDot = clean.lastIndexOf(".");
  const lastComma = clean.lastIndexOf(",");
  let numStr;
  if (lastDot >= 0 && lastComma >= 0) {
    numStr = lastDot > lastComma ? clean.replace(/,/g, "") : clean.replace(/\./g, "").replace(",", ".");
  } else if (lastComma >= 0) {
    numStr = clean.replace(",", ".");
  } else if (lastDot >= 0) {
    numStr = moneda === "CLP" || moneda === "PEN" ? clean.replace(/\./g, "") : clean;
  } else {
    numStr = clean;
  }
  if (numStr.endsWith(".")) return numStr;
  const num = parseFloat(numStr);
  return isNaN(num) ? "" : num;
};

const formatFechaDisplay = (fechaStr) => {
  if (!fechaStr) return "-";
  const [y, m, d] = fechaStr.substring(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const PlanPagoEdificioModal = ({ isOpen, onClose, edificio, pais, userId, onSuccess }) => {
  const { formatAmount } = useCurrency();

  const [monto, setMonto] = useState("");
  const [fechaAcordada, setFechaAcordada] = useState("");
  const [loading, setLoading] = useState(false);
  const [pagosExistentes, setPagosExistentes] = useState([]);
  const [editandoId, setEditandoId] = useState(null);
  const [fechaEdicion, setFechaEdicion] = useState("");
  const [montoEdicion, setMontoEdicion] = useState("");

  const monedaEdificio = edificio?.moneda || (pais === "Chile" ? "CLP" : "PEN");
  const hoyStr = new Date().toISOString().split("T")[0];

  const cargarExistentes = useCallback(async () => {
    if (!edificio?.edificioId || !pais) return;
    try {
      const todos = await getPlanPagosEdificios(pais);
      setPagosExistentes(todos.filter((p) => p.edificio_id === edificio.edificioId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error cargando pagos planificados:", err);
      setPagosExistentes([]);
    }
  }, [edificio?.edificioId, pais]);

  useEffect(() => {
    if (!isOpen || !edificio) return;
    setFechaAcordada(edificio.proximaFecha || hoyStr);
    setEditandoId(null);
    setFechaEdicion("");
    setMontoEdicion("");
    cargarExistentes();
  }, [isOpen, edificio, cargarExistentes, hoyStr]);

  // Monto del formulario "Registrar pago" se autoajusta al saldo restante:
  // saldoPendiente − suma de planes existentes. Cada vez que se crea, anula o
  // edita un plan, pagosExistentes cambia y este efecto recalcula el residual.
  useEffect(() => {
    if (!edificio) return;
    const totalPlanificado = pagosExistentes.reduce((sum, p) => sum + Number(p.monto || 0), 0);
    const restante = Math.round(Number(edificio.saldoPendiente || 0) - totalPlanificado);
    setMonto(restante > 0 ? restante : 0);
  }, [pagosExistentes, edificio]);

  const handleRegistrar = async () => {
    if (!fechaAcordada) return toast.error("Ingresá una fecha");
    const montoNum = typeof monto === "string" ? 0 : monto;
    if (!montoNum || montoNum <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }

    setLoading(true);
    try {
      const row = await crearPlanPagoEdificio(
        {
          edificio_id: edificio.edificioId,
          pais,
          monto: montoNum,
          fecha_acordada: fechaAcordada,
          nota: null,
          created_by: userId || null,
        },
        { modo: MODOS_VALIDACION.DATO_DOCUMENTO }
      );
      toast.success("Pago planificado registrado");
      // El modal queda abierto para más operaciones → refrescar su propia lista (como anular/editar).
      await cargarExistentes();
      // Patch puntual (Fase 2B): el padre agrega el row nuevo (con su id real) a planPagosEdificios.
      onSuccess?.({ op: "crear", row });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error registrando pago:", err);
      toast.error("Error al registrar el pago");
    } finally {
      setLoading(false);
    }
  };

  const handleAnular = async (id) => {
    setLoading(true);
    try {
      await anularPlanPagoEdificio(id);
      toast.success("Pago anulado");
      await cargarExistentes();
      // Patch puntual (Fase 2B): el padre saca el row del estado — un anulado desaparece del array
      // (getPlanPagosEdificios trae solo estado='pendiente'), igual que produciría el refetch.
      onSuccess?.({ op: "anular", id });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error anulando pago:", err);
      toast.error("Error al anular el pago");
    } finally {
      setLoading(false);
    }
  };

  const handleIniciarEdicion = (plan) => {
    setEditandoId(plan.id);
    setFechaEdicion(plan.fecha_acordada || "");
    setMontoEdicion(Number(plan.monto || 0));
  };

  const handleCancelarEdicion = () => {
    setEditandoId(null);
    setFechaEdicion("");
    setMontoEdicion("");
  };

  const handleGuardarEdicion = async () => {
    if (!fechaEdicion) return toast.error("Ingresá una fecha");
    const montoNum = typeof montoEdicion === "string" ? 0 : montoEdicion;
    if (!montoNum || montoNum <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setLoading(true);
    try {
      // F1 (B.2b, 2026-09-26): DATO_DOCUMENTO PROVISIONAL — este editor (uno de los "8 modales
      // viejos", permanente por 10.232) no valida hoy piso ni día hábil. pendiente Fase 5.
      const row = await actualizarPlanPagoEdificio(
        editandoId,
        { fecha_acordada: fechaEdicion, monto: montoNum },
        { modo: MODOS_VALIDACION.DATO_DOCUMENTO }
      );
      toast.success("Pago actualizado");
      setEditandoId(null);
      setFechaEdicion("");
      setMontoEdicion("");
      await cargarExistentes();
      // Patch puntual (Fase 2B): el padre actualiza el row por id (fecha/monto).
      onSuccess?.({ op: "editar", row });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error actualizando pago:", err);
      toast.error("Error al actualizar el pago");
    } finally {
      setLoading(false);
    }
  };

  if (!edificio) return null;

  const labelBoton = pagosExistentes.length === 0 ? "Registrar pago" : "Agregar pago";
  const totalPlanificado = pagosExistentes.reduce((sum, p) => sum + Number(p.monto || 0), 0);
  const saldoRestante = Math.round(Number(edificio.saldoPendiente || 0) - totalPlanificado);

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Pago a {edificio.edificioNombre}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div
          style={{
            padding: "10px 12px",
            marginBottom: 16,
            borderRadius: 6,
            background: "#fdecea",
            border: "1px solid #f5c6cb",
            fontSize: "0.85rem",
            color: "#c0392b",
            fontWeight: 600,
          }}
        >
          Saldo pendiente: {formatAmount(edificio.saldoPendiente, monedaEdificio)}
        </div>

        {pagosExistentes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h6 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 8, color: "#2c3e50" }}>
              Pagos planificados
            </h6>
            <div style={{ overflowX: "auto", width: "100%" }}>
              <table className="table table-sm table-bordered" style={{ fontSize: "0.83rem", marginBottom: 0 }}>
                <thead className="table-dark">
                  <tr>
                    <th>Fecha</th>
                    <th style={{ textAlign: "right" }}>Monto</th>
                    <th style={{ textAlign: "center" }} />
                  </tr>
                </thead>
                <tbody>
                  {pagosExistentes.map((p) => {
                    const enEdicion = editandoId === p.id;
                    return (
                      <tr key={p.id}>
                        <td>
                          {enEdicion ? (
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={fechaEdicion}
                              min={hoyStr}
                              onChange={(e) => setFechaEdicion(e.target.value)}
                              disabled={loading}
                              style={{ maxWidth: 150 }}
                            />
                          ) : (
                            formatFechaDisplay(p.fecha_acordada)
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {enEdicion ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={formatMontoDisplay(montoEdicion, pais)}
                              onChange={(e) => setMontoEdicion(parseMontoInput(e.target.value, monedaEdificio))}
                              disabled={loading}
                              style={{ maxWidth: 130, marginLeft: "auto", textAlign: "right" }}
                            />
                          ) : (
                            formatAmount(p.monto, monedaEdificio)
                          )}
                        </td>
                        <td style={{ textAlign: "center", whiteSpace: "nowrap", width: 1 }}>
                          {enEdicion ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={handleGuardarEdicion}
                                disabled={loading}
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "1px 8px",
                                  whiteSpace: "nowrap",
                                  marginRight: 4,
                                }}
                              >
                                Guardar
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleCancelarEdicion}
                                disabled={loading}
                                style={{ fontSize: "0.7rem", padding: "1px 8px", whiteSpace: "nowrap" }}
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleIniciarEdicion(p)}
                                disabled={loading}
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "1px 8px",
                                  whiteSpace: "nowrap",
                                  marginRight: 4,
                                }}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleAnular(p.id)}
                                disabled={loading}
                                style={{ fontSize: "0.7rem", padding: "1px 8px", whiteSpace: "nowrap" }}
                              >
                                Anular
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pagosExistentes.length > 0 && (
          <div
            style={{
              padding: "8px 12px",
              background: saldoRestante > 0 ? "#fff3cd" : "#d4edda",
              borderRadius: 6,
              fontSize: "0.85rem",
              marginBottom: 16,
            }}
          >
            {saldoRestante > 0
              ? `Sin planificar: ${formatAmount(saldoRestante, monedaEdificio)}`
              : "Deuda 100% planificada ✓"}
          </div>
        )}

        {saldoRestante > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h6 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 12, color: "#2c3e50" }}>Registrar pago</h6>
            <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Fecha acordada
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fechaAcordada}
                  min={hoyStr}
                  onChange={(e) => setFechaAcordada(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Monto ({monedaEdificio})
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formatMontoDisplay(monto, pais)}
                  onChange={(e) => setMonto(parseMontoInput(e.target.value, monedaEdificio))}
                  placeholder={String(Math.round(Number(edificio.saldoPendiente) || 0))}
                  disabled={loading}
                />
                <div style={{ fontSize: "0.7rem", color: "#888", marginTop: 2 }}>Podés ingresar un monto parcial</div>
              </div>
            </div>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleRegistrar} disabled={loading}>
              {loading ? "Registrando..." : labelBoton}
            </button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanPagoEdificioModal;
